<template>
  <BittsDrawer
    v-if="webhook"
    class="api-webhook-settings-drawer"
    :visible="true"
    :title="webhook.name"
    width="600"
    @closed="handleClose"
  >
    <template #content>
      <section class="api-webhook-settings-drawer__section">
        <BittsContainer class="api-webhook-settings-drawer__enable">
          <BittsSwitch
            v-model="isWebhookEnabled"
            label="Enable Webhook"
            description="Turn on to start receiving webhook events"
            :disabled="!isWebhookTested || !canManageIntegrations"
          />
        </BittsContainer>
        <BittsAlert
          v-if="!isWebhookTested"
          color="warning"
          message="Test required"
          description="Webhook must be tested successfully before it can be enabled"
        />
        <div class="api-webhook-settings-drawer__buttons">
          <BittsButton
            text="Edit Configuration"
            type="neutral"
            variant="outline"
            size="small"
            :left-icon="['fak', 'settings']"
            :disabled="isEditLocked"
            @click="activeOverlay = 'config'"
          />
          <BittsButton
            v-if="!isWebhookTested"
            text="Test webhook"
            type="neutral"
            variant="outline"
            size="small"
            :loading="isTesting"
            :disabled="!canManageIntegrations || isSaving"
            @click="handleTestClick"
          />
        </div>
      </section>
      <BittsDivider class="my-0" />
      <section class="api-webhook-settings-drawer__section">
        <ApiDetailRow label="Webhook status">
          <template #value>
            <BittsBadge
              :status="statusConfig.status"
              :text="statusConfig.text"
            />
          </template>
        </ApiDetailRow>
        <ApiDetailRow label="Webhook URL">
          <template #value>
            <BittsTooltip
              placement="bottom"
              :mount-to-body="true"
              class="api-webhook-settings-drawer__url"
            >
              <span class="api-webhook-settings-drawer__url-text">
                {{ webhook.target_url }}
              </span>
              <template #title>{{ webhook.target_url }}</template>
            </BittsTooltip>
          </template>
          <template #action>
            <BittsButton
              type="neutral"
              variant="ghost"
              size="x-small"
              :center-icon="['fak', 'copy']"
              aria-label="Copy webhook URL"
              @click="copyWebhookUrl"
            />
          </template>
        </ApiDetailRow>
        <ApiDetailRow label="Last signal pushed" :value="lastSignalPushed" />
        <ApiDetailRow
          label="Webhook configured by"
          :value="getUserName(webhook.created_by)"
        />
        <ApiDetailRow
          label="Webhook last edited by"
          :value="getUserName(webhook.updated_by)"
        />
        <BittsButton
          text="Rotate signing secret"
          type="neutral"
          variant="outline"
          size="small"
          class="w-fit"
          :disabled="isEditLocked"
          @click="activeOverlay = 'rotate-secret'"
        />
      </section>
      <BittsDivider class="my-0" />
      <section class="api-webhook-settings-drawer__section">
        <div>
          <h3 class="api-webhook-settings-drawer__push-title">
            Customize Data in Push
          </h3>
          <p class="api-webhook-settings-drawer__push-description">
            Control what data shows in this webhook
          </p>
        </div>
        <IntegrationSettings
          :population-settings="localProfileSettings ?? webhookProfileSettings"
          setting-type="webhook"
          :hide-offline-partners="true"
          :is-disabled="isPushDisabled"
          @save:changes="onWebhookSettingsUpdated"
          @update:exclude-partnerships-created-since="
            onUpdateExcludePartnershipsCreatedSince
          "
        >
          <template v-if="hasNNAEvent" #net-new-accounts>
            <BittsCollapse
              v-model:is-open="isNetNewAccountsOpen"
              chevron-placement="right"
              :toggle-on-body-click="false"
              aria-label="Net New Accounts"
              class="api-webhook-settings-drawer__net-new"
              data-testid="net-new-accounts-section"
            >
              <template #title>
                <div class="api-webhook-settings-drawer__net-new-title">
                  <div class="flex items-center gap-4">
                    <h6>Net New Accounts</h6>
                    <BittsTooltip placement="top" :mount-to-body="true">
                      <FontAwesomeIcon
                        :icon="['fak', 'information-circle']"
                        class="text-neutral-accent h-12 w-12 mb-2"
                      />
                      <template #title>
                        Your Partners' customers that aren't identified in your
                        populations or in your CRM.
                      </template>
                    </BittsTooltip>
                  </div>
                  <span>{{
                    isNetNewAccountsOpen ? 'Collapse' : 'Expand'
                  }}</span>
                </div>
              </template>
              <BittsCheckbox
                class="pt-12 pl-16"
                :checked="isNetNewAccountsEnabled"
                :disabled="isPushDisabled"
                label="Net New Accounts"
                @input="
                  (isChecked: boolean) => (isNetNewAccountsEnabled = isChecked)
                "
              />
            </BittsCollapse>
          </template>
        </IntegrationSettings>
      </section>
    </template>
    <template #footer>
      <div class="api-webhook-settings-drawer__footer">
        <BittsBanner
          v-if="hasUnsavedChanges && !isWebhookEnabled"
          variant="warning"
          message="Webhook is currently disabled"
          :icon="['fak', 'information-circle']"
          class="api-webhook-settings-drawer__disabled-banner"
        />
        <ApiDrawerFooter
          remove-text="Remove webhook"
          :is-saving="isSaving"
          :is-save-disabled="!canSave"
          :is-remove-disabled="isEditLocked"
          @save="handleSaveClick"
          @remove="activeOverlay = 'delete'"
        />
      </div>
    </template>
  </BittsDrawer>

  <BittsModal
    v-if="activeOverlay === 'rotate-secret'"
    variant="confirm"
    title="Rotate Secret"
    description="Generating a new secret will disable your current one. Until setup is complete, signals will not be delivered. Please finish updating your endpoint as quickly as possible to avoid gaps in data."
    primary-button-text="Generate new secret"
    secondary-button-text="Cancel"
    @action="activeOverlay = 'secret'"
    @closed="closeOverlay"
  />

  <NewSecretGeneratedModal
    v-if="activeOverlay === 'secret'"
    :webhook-id="resourceId"
    @closed="closeOverlay"
  />

  <WebhookConfigModal
    v-if="activeOverlay === 'config' && webhook"
    :is-edit="true"
    :webhook-data="webhook"
    @closed="closeOverlay"
  />

  <BittsModal
    v-if="activeOverlay === 'delete'"
    variant="confirm"
    title="Remove webhook"
    description="Are you sure you want to remove this webhook? This action cannot be undone."
    primary-button-text="Remove webhook"
    primary-button-type="danger"
    secondary-button-text="Cancel"
    :action-loading="isDeleting"
    @action="handleDelete"
    @closed="closeOverlay"
  />

  <UnsavedChangesModal
    v-if="activeOverlay === 'unsaved-changes'"
    @discard="handleDiscard"
    @closed="closeOverlay"
  />
</template>

<script setup lang="ts">
import {
  BittsAlert,
  BittsBadge,
  BittsBanner,
  BittsButton,
  BittsCheckbox,
  BittsCollapse,
  BittsContainer,
  BittsDivider,
  BittsDrawer,
  BittsModal,
  BittsSwitch,
  BittsTooltip,
} from '@crossbeam/bitts';
import { EVENT_SITES } from '@crossbeam/itly';

import { useHead } from '@unhead/vue';
import { DateTime } from 'luxon';
import { computed, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';

import IntegrationSettings from '@/components/integrations/IntegrationSettings.vue';
import NewSecretGeneratedModal from '@/components/integrations/NewSecretGeneratedModal.vue';
import UnsavedChangesModal from '@/components/integrations/UnsavedChangesModal.vue';
import WebhookConfigModal from '@/components/integrations/WebhookConfigModal.vue';

import useWebhookSettings from '@/composables/useWebhookSettings';
import { useTeamStore, useWebhooksStore } from '@/stores';
import { WEBHOOK_STATUS_CONFIG } from '@/types/webhooks';

import ApiDetailRow from '../components/ApiDetailRow.vue';
import ApiDrawerFooter from '../components/ApiDrawerFooter.vue';
import useApiControlsTracking from '../useApiControlsTracking';

const { resourceId } = defineProps<{
  resourceId: string;
}>();

const emit = defineEmits<{
  closed: [];
}>();

const route = useRoute();
const { getUserName } = useTeamStore();
const webhooksStore = useWebhooksStore();

const {
  currentWebhook: webhook,
  localProfileSettings,
  webhookProfileSettings,
  isWebhookEnabled,
  isNetNewAccountsEnabled,
  isWebhookTested,
  hasUnsavedChanges,
  canManageIntegrations,
  canSave,
  hasNNAEvent,
  isSaving,
  isTesting,
  initializeWebhookState,
  saveWebhook,
  deleteWebhook,
  testWebhook,
  discardChanges,
  copyWebhookUrl,
  onWebhookSettingsUpdated,
  onUpdateExcludePartnershipsCreatedSince,
} = useWebhookSettings(resourceId);

const { trackApiControlAction } = useApiControlsTracking();

useHead({
  title: computed(
    () => `${webhook.value?.name ?? 'Webhook'} - Webhooks - API - Crossbeam`,
  ),
});

type WebhookOverlayType =
  | 'config'
  | 'rotate-secret'
  | 'secret'
  | 'delete'
  | 'unsaved-changes';

const activeOverlay = ref<WebhookOverlayType | null>(null);
const isNetNewAccountsOpen = ref(true);
const isDeleting = ref(false);

function closeOverlay() {
  activeOverlay.value = null;
}

const statusConfig = computed(() =>
  webhook.value
    ? WEBHOOK_STATUS_CONFIG[webhook.value.status]
    : WEBHOOK_STATUS_CONFIG.untested,
);

const isEditLocked = computed(
  () => isTesting.value || isSaving.value || !canManageIntegrations.value,
);

const isPushDisabled = computed(
  () => !isWebhookTested.value || isEditLocked.value,
);

const lastSignalPushed = computed(() => {
  const sentAt = webhook.value?.latest_message_sent_at;
  return sentAt
    ? DateTime.fromISO(sentAt).toFormat("LLL dd, yyyy 'at' h:mm a")
    : '--';
});

function handleTestClick() {
  trackApiControlAction({
    eventSite: EVENT_SITES.API_WEBHOOK_SETTINGS_DRAWER,
    type: 'test_clicked',
    resource: 'webhook',
    resourceId,
  });
  testWebhook();
}

function handleSaveClick() {
  trackApiControlAction({
    eventSite: EVENT_SITES.API_WEBHOOK_SETTINGS_DRAWER,
    type: 'settings_saved',
    resource: 'webhook',
    resourceId,
    metadata: { is_enabled: isWebhookEnabled.value },
  });
  saveWebhook();
}

function handleClose() {
  if (hasUnsavedChanges.value) activeOverlay.value = 'unsaved-changes';
  else emit('closed');
}

function handleDiscard() {
  discardChanges();
  closeOverlay();
  emit('closed');
}

async function handleDelete() {
  isDeleting.value = true;
  try {
    const hasDeleted = await deleteWebhook();
    if (!hasDeleted) return;
    closeOverlay();
    emit('closed');
  } finally {
    isDeleting.value = false;
  }
}

onMounted(async () => {
  await webhooksStore.readySync;
  if (!webhook.value) {
    emit('closed');
    return;
  }
  initializeWebhookState();
  if (route.query.action === 'rotate-secret' && canManageIntegrations.value) {
    activeOverlay.value = 'rotate-secret';
  }
});
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-webhook-settings-drawer__section {
  @apply flex flex-col gap-16 p-16;
}

.api-webhook-settings-drawer__enable {
  @apply py-12;
}

.api-webhook-settings-drawer__buttons {
  @apply flex gap-12;
}

.api-webhook-settings-drawer__push-title {
  @apply text-base font-bold text-neutral-text-strong m-0;
}

.api-webhook-settings-drawer__push-description {
  @apply text-sm text-neutral-text m-0 mt-4;
}

.api-webhook-settings-drawer__url {
  @apply block min-w-0;
}

.api-webhook-settings-drawer__url-text {
  @apply block truncate;
}

.api-webhook-settings-drawer__footer {
  @apply flex flex-col w-full;
}

.api-webhook-settings-drawer__disabled-banner {
  @apply -mx-16 -mt-16 mb-16 w-[calc(100%+32px)];
}

.api-webhook-settings-drawer__net-new {
  @apply bg-neutral-background-weak/50 rounded-lg px-12 py-8 mt-16;
}

.api-webhook-settings-drawer__net-new-title {
  @apply flex items-center justify-between w-full;

  h6 {
    @apply text-info-text text-base/6 font-bold m-0;
  }

  span {
    @apply text-neutral-text-weak text-base/6;
  }
}
</style>
